import { List } from "immutable";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import interfaceIdentifiers from "../../../constants/interfaceIdentifiers";
import { appStateSelectors } from "../../../modules/appState";
import { typeSelectors } from "../../../modules/definition/types";
import displayCacheSelectors from "../../../modules/display/selectors";
import { filterSelectors } from "../../../modules/filters";
import { itemSelectors } from "../../../modules/items";
import { TreeViewItemRecord } from "../../../types/immutableRecords/display/treeView/TreeViewItemRecord";

const mapDisplayItemToTreeItem = (displayItem, relationshipsFilterByType, mainType, typesById, index) => {
  const isMainType = displayItem.type === mainType;
  const itemType = typesById.get(displayItem.type);
  const relationshipsFilter = relationshipsFilterByType.get(displayItem.type);
  const filterMatch = relationshipsFilter ? relationshipsFilter.terms.contains(displayItem.id) : false;
  const displayLabel = typeSelectors.getTypeLabelDisplay(itemType);
  const placeholder = `${itemType.label} ${displayLabel}`;

  return TreeViewItemRecord({
    id: displayItem.id,
    index,
    type: displayItem.type,
    label: displayItem.label,
    placeholder,
    icon: isMainType ? "circle-f" : itemType.icon,
    iconColor: isMainType ? displayItem.color : itemType.color,
    statusIcon: filterMatch ? "workviewFilterFilledActive" : "workviewFilterFilled",
    statusColor: filterMatch ? "iconActive" : "iconFade",
    statusLabel: filterMatch ? "Remove filter" : "Filter focused view",
    statusIconIdentifier: interfaceIdentifiers.ENTITY_PANEL_FILTER,
    children: displayItem.children.map((c, i) =>
      mapDisplayItemToTreeItem(c, relationshipsFilterByType, mainType, typesById, i)
    )
  });
};

const convertItemGroupsToDisplayTree = (thisLevel, displayItemMap, itemGroupsByParentId) => {
  return thisLevel
    ? thisLevel.map(id =>
      displayItemMap
        .get(id)
        .set(
          "children",
          convertItemGroupsToDisplayTree(itemGroupsByParentId.get(id), displayItemMap, itemGroupsByParentId)
        )
    )
    : List();
};

/**
 * @param {TimelineRecord} state
 * @param {string} typeId
 * @param {string} activeViewId
 */
const getDisplayItemsTreeOfType = createSelector(
  displayCacheSelectors.getDisplayItemsById,
  itemSelectors.getItemTreeGroupsForTypeAndSuperTypesFromState,
  (_state, typeId, _activeViewId) => typeId,
  typeSelectors.getTypesById,
  (state, _typeId, activeViewId) => filterSelectors.getFilterWithId(state, activeViewId),
  (displayItemMap, itemGroupsByParentId, typeId, typesById, viewFilter) => {
    // Include all super items
    const relationshipsFilterByType = viewFilter.relationshipsByType;
    const topLevelTree = convertItemGroupsToDisplayTree(
      itemGroupsByParentId.get(""),
      displayItemMap,
      itemGroupsByParentId
    );
    const result = topLevelTree.map((item, index) =>
      mapDisplayItemToTreeItem(item, relationshipsFilterByType, typeId, typesById, index)
    );
    return result;
  }
);

const flattenTreeItem = (itemsList, expandedItems) => {
  return itemsList.reduce((resultList, i) => {
    let list = resultList.push(i.id);
    if (i.children && i.children.size > 0 && expandedItems.includes(i.id)) {
      list = list.concat(flattenTreeItem(i.children, expandedItems));
    }
    return list;
  }, List());
};

const getFlatTreeItemIds = createCachedSelector(
  getDisplayItemsTreeOfType,
  appStateSelectors.getExpandedEntityItems,
  (treeItems, expandedItems) => {
    return flattenTreeItem(treeItems, expandedItems);
  }
)((_state, typeId, activeViewId) => typeId + activeViewId);

export { getDisplayItemsTreeOfType, getFlatTreeItemIds };
